import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import EventsDesk, {
    DeskRibbon,
    DeskPanel,
    DeskTitle,
    DeskNote,
    DeskStat,
    DeskEmpty,
    DESK_BUTTON_CLASS
} from '../../../components/staff/events/EventsDesk';
import {
    EVENT_TYPES,
    WEEKDAY_SHORT,
    addDaysToKey,
    dayKey,
    durationLabel,
    offsetLabel,
    pad,
    readStoredZone,
    typeColor,
    zonedToUtc
} from '../../../components/staff/events/eventsShared';
import { getStaffEventPeople, getStaffEventStats } from '../../../services/api';

const RANGES = [
    { key: '7', label: 'Last 7 days', back: 7, forward: 0 },
    { key: '30', label: 'Last 30 days', back: 30, forward: 0 },
    { key: '90', label: 'Last 90 days', back: 90, forward: 0 },
    { key: '365', label: 'Last year', back: 365, forward: 0 },
    { key: 'ahead', label: 'Next 30 days', back: 0, forward: 30 },
    { key: 'all', label: 'Everything', back: null, forward: null }
];

const monthLabel = (key) => {
    const [year, month] = key.split('-').map(Number);

    return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString(undefined, {
        month: 'short',
        year: '2-digit',
        timeZone: 'UTC'
    });
};

function Bars({ rows, emptyLabel }) {
    const max = rows.reduce((highest, row) => Math.max(highest, row.value), 0);

    if (!rows.length || max === 0) return <DeskEmpty>{emptyLabel}</DeskEmpty>;

    return (
        <div className="flex flex-col gap-[3px]">
            {rows.map(row => (
                <div key={row.label} className="flex items-center gap-2">
                    <span className="w-[54px] shrink-0 text-right font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500">
                        {row.label}
                    </span>
                    <div className="h-[14px] flex-1 bg-gray-100">
                        <div
                            className="h-full"
                            style={{
                                width: `${Math.max(row.value ? 3 : 0, (row.value / max) * 100)}%`,
                                backgroundColor: row.color || '#e8871e'
                            }}
                        ></div>
                    </div>
                    <span className="w-[36px] shrink-0 text-right text-[12px] text-gray-600">{row.value}</span>
                </div>
            ))}
        </div>
    );
}

function LeaderTable({ rows, columns, emptyLabel }) {
    if (!rows.length) return <DeskEmpty>{emptyLabel}</DeskEmpty>;

    return (
        <div className="max-h-[360px] overflow-y-auto">
            <table className="w-full text-[13px]">
                <thead className="sticky top-0 bg-offwhite">
                    <tr className="border-b border-gray-200 text-left">
                        {columns.map(column => (
                            <th
                                key={column.key}
                                className={`py-1 font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500 ${column.align || ''}`}
                            >
                                {column.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={row.key || index} className="border-b border-gray-100 last:border-0">
                            {columns.map(column => (
                                <td key={column.key} className={`py-[5px] text-gray-800 ${column.align || ''}`}>
                                    {column.render(row, index)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Stats({ userData }) {
    const [zone] = useState(readStoredZone);

    const [rangeKey, setRangeKey] = useState('30');
    const [hostFilter, setHostFilter] = useState('');
    const [creatorFilter, setCreatorFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    const [stats, setStats] = useState(null);
    const [people, setPeople] = useState({ hosts: [], creators: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const range = useMemo(() => {
        const preset = RANGES.find(entry => entry.key === rangeKey) || RANGES[1];
        if (preset.back === null) return { from: '', to: '', label: 'All time' };

        const todayKey = dayKey(new Date(), zone);
        const fromKey = addDaysToKey(todayKey, -preset.back);
        const toKey = addDaysToKey(todayKey, preset.forward + 1);

        return {
            from: zonedToUtc(fromKey, '00:00', zone).toISOString(),
            to: zonedToUtc(toKey, '00:00', zone).toISOString(),
            label: preset.label
        };
    }, [rangeKey, zone]);

    useEffect(() => {
        let active = true;

        setLoading(true);

        getStaffEventStats({
            from: range.from,
            to: range.to,
            host: hostFilter,
            addedBy: creatorFilter,
            type: typeFilter
        })
            .then(result => {
                if (!active) return;

                if (result?.success) {
                    setStats(result);
                    setError('');
                } else {
                    setError(result?.message || 'Could not load the numbers.');
                }

                setLoading(false);
            })
            .catch(() => {
                if (!active) return;
                setError('Could not load the numbers.');
                setLoading(false);
            });

        return () => { active = false; };
    }, [range, hostFilter, creatorFilter, typeFilter]);

    useEffect(() => {
        let active = true;

        getStaffEventPeople()
            .then(result => {
                if (active && result?.success) {
                    setPeople({ hosts: result.hosts || [], creators: result.creators || [] });
                }
            })
            .catch(() => {});

        return () => { active = false; };
    }, []);

    const totals = stats?.totals;

    const weekdayRows = useMemo(
        () => (stats?.byWeekdayUtc || []).map((value, index) => ({
            label: WEEKDAY_SHORT[index],
            value
        })),
        [stats]
    );

    const hourRows = useMemo(
        () => (stats?.byHourUtc || [])
            .map((value, hour) => ({ label: `${pad(hour)}:00`, value }))
            .filter(row => row.value > 0),
        [stats]
    );

    const monthRows = useMemo(
        () => (stats?.byMonthUtc || []).map(row => ({
            label: monthLabel(`${row.month}-01`),
            value: row.events
        })),
        [stats]
    );

    const typeRows = useMemo(
        () => (stats?.byType || []).map(row => ({
            label: row.name,
            value: row.events,
            color: typeColor(row.name)
        })),
        [stats]
    );

    if (!userData?.hasStaffRole) {
        return <Navigate to="/" replace />;
    }

    return (
        <EventsDesk>
            <DeskRibbon>
                <DeskTitle>Statistics</DeskTitle>

                <div className="flex flex-wrap items-center gap-1">
                    {RANGES.map(entry => (
                        <button
                            key={entry.key}
                            type="button"
                            onClick={() => setRangeKey(entry.key)}
                            className={`${DESK_BUTTON_CLASS} ${rangeKey === entry.key ? '!bg-gray-800 !text-white' : ''}`}
                        >
                            {entry.label}
                        </button>
                    ))}
                </div>

                {loading && <DeskNote>Loading...</DeskNote>}
            </DeskRibbon>

            <DeskPanel title="Narrow it down">
                <div className="flex flex-wrap items-end gap-3">
                    <label className="flex flex-col gap-1">
                        <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500">
                            Host
                        </span>
                        <select
                            value={hostFilter}
                            onChange={(e) => setHostFilter(e.target.value)}
                            className="rounded border border-gray-300 px-2 py-1 text-[13px] text-gray-800"
                        >
                            <option value="">Everyone</option>
                            {people.hosts.map(host => (
                                <option key={host.name} value={host.name}>
                                    {host.name} ({host.events})
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex flex-col gap-1">
                        <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500">
                            Set up by
                        </span>
                        <select
                            value={creatorFilter}
                            onChange={(e) => setCreatorFilter(e.target.value)}
                            className="rounded border border-gray-300 px-2 py-1 text-[13px] text-gray-800"
                        >
                            <option value="">Anyone</option>
                            {people.creators.map(creator => (
                                <option key={creator.userId} value={creator.userId}>
                                    {creator.name} ({creator.events})
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="flex flex-col gap-1">
                        <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500">
                            Type
                        </span>
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="rounded border border-gray-300 px-2 py-1 text-[13px] text-gray-800"
                        >
                            <option value="">All types</option>
                            {EVENT_TYPES.map(type => (
                                <option key={type.name} value={type.name}>{type.name}</option>
                            ))}
                        </select>
                    </label>

                    {(hostFilter || creatorFilter || typeFilter) && (
                        <button
                            type="button"
                            onClick={() => { setHostFilter(''); setCreatorFilter(''); setTypeFilter(''); }}
                            className={DESK_BUTTON_CLASS}
                        >
                            Clear filters
                        </button>
                    )}

                    <DeskNote>
                        {range.label}, counted against your zone ({offsetLabel(zone)}). Breakdowns by day and hour are UTC.
                    </DeskNote>
                </div>
            </DeskPanel>

            {error && (
                <p className="border border-red-300 bg-red-50 px-3 py-2 text-[13px] text-red-700">{error}</p>
            )}

            {totals && (
                <div className="flex flex-wrap gap-2">
                    <DeskStat label="Events" value={totals.events} />
                    <DeskStat label="Still to come" value={totals.upcoming} tone="good" />
                    <DeskStat label="Already run" value={totals.finished} />
                    <DeskStat label="Hosts" value={totals.hosts} />
                    <DeskStat label="Rooms used" value={totals.roomOwners} hint="by owner" />
                    <DeskStat label="Set up by" value={totals.creators} hint="staff members" />
                    <DeskStat label="Total time" value={durationLabel(totals.minutes)} />
                    <DeskStat label="Average length" value={durationLabel(totals.averageMinutes)} />
                </div>
            )}

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <DeskPanel title="Who is hosting">
                    <LeaderTable
                        rows={(stats?.byHost || []).map(row => ({ ...row, key: row.name }))}
                        emptyLabel="No events in this window"
                        columns={[
                            { key: 'rank', label: '#', render: (row, index) => index + 1 },
                            { key: 'name', label: 'Host', render: row => row.name },
                            { key: 'events', label: 'Events', align: 'text-right', render: row => row.events },
                            { key: 'time', label: 'Time', align: 'text-right', render: row => durationLabel(row.minutes) },
                            {
                                key: 'last',
                                label: 'Last one',
                                align: 'text-right',
                                render: row => (row.lastEvent ? dayKey(row.lastEvent, zone) : '')
                            }
                        ]}
                    />
                </DeskPanel>

                <DeskPanel title="Who is setting them up">
                    <LeaderTable
                        rows={(stats?.byCreator || []).map(row => ({ ...row, key: row.userId || row.name }))}
                        emptyLabel="No events in this window"
                        columns={[
                            { key: 'rank', label: '#', render: (row, index) => index + 1 },
                            { key: 'name', label: 'Staff member', render: row => row.name },
                            { key: 'events', label: 'Events', align: 'text-right', render: row => row.events }
                        ]}
                    />
                </DeskPanel>

                <DeskPanel title="By event type">
                    <Bars rows={typeRows} emptyLabel="No events in this window" />
                </DeskPanel>

                <DeskPanel title="Busiest day (UTC)">
                    <Bars rows={weekdayRows} emptyLabel="No events in this window" />
                </DeskPanel>

                <DeskPanel title="Busiest hour (UTC)">
                    <Bars rows={hourRows} emptyLabel="No events in this window" />
                </DeskPanel>

                <DeskPanel title="Events per month (UTC)">
                    <Bars rows={monthRows} emptyLabel="No events in this window" />
                </DeskPanel>

                <DeskPanel title="Rooms in use" className="lg:col-span-2">
                    <LeaderTable
                        rows={(stats?.byRoomOwner || []).map(row => ({ ...row, key: row.name }))}
                        emptyLabel="No events in this window"
                        columns={[
                            { key: 'name', label: 'Room owner', render: row => row.name },
                            { key: 'events', label: 'Events hosted there', align: 'text-right', render: row => row.events }
                        ]}
                    />
                </DeskPanel>
            </div>

            {totals?.firstEvent && (
                <DeskNote>
                    Window runs from {dayKey(totals.firstEvent, zone)} to {dayKey(totals.lastEvent, zone)}.
                </DeskNote>
            )}
        </EventsDesk>
    );
}

export default Stats;
